import React from 'react';
import {defineMessages} from 'react-intl';
import type {
    DateRangeFilterValue,
    FilterGroupNewFilter,
    FilterProps,
    FiltersProps,
    QueryFilterAdapterModel,
    QueryFilterAdapterProps,
} from '@igp/shared/components/filter';
import {DateRangeFilter, FilterGroupNew, SelectFilter, TextFilterFullWidth, withQueryFilter} from '@igp/shared/components/filter';
import type {SelectOption} from '@igp/shared/components/select';
import {OperationType, ResourceType} from '@igp/shared/contract/pam';
import {useLocale} from '@igp/shared/localization';
import type {Locale} from '@igp/shared/style';
import {addDaysToDate, getTodayAsDate} from '@igp/shared/utils';

import type {
    KeycloakLogFilterKeys,
    KeycloakLogFilterName,
    KeycloakLogFiltersViewModel,
    KeycloakLogTextFilterKeys,
} from '@features/keycloak-log/types';

import {operationTypeLocalized, resourceTypeLocalized} from '../constants';

const localized = defineMessages({
    operator: {
        id: 'KeycloakLogFilters_operator',
        defaultMessage: 'Operator',
    },
    targetUser: {
        id: 'KeycloakLogFilters_targetUser',
        defaultMessage: 'Target User',
    },
    operatorPlaceholder: {
        id: 'KeycloakLogFilters_operatorPlaceholder',
        defaultMessage: 'Search by operator',
    },
    targetUserPlaceholder: {
        id: 'KeycloakLogFilters_targetUserPlaceholder',
        defaultMessage: 'Search by target user',
    },
    operationType: {
        id: 'KeycloakLogFilters_operationType',
        defaultMessage: 'Action',
    },
    resourceType: {
        id: 'KeycloakLogFilters_resourceType',
        defaultMessage: 'Resource',
    },
    date: {
        id: 'KeycloakLogFilters_date',
        defaultMessage: 'Date',
    },
});

const operationTypeOptions: SelectOption<OperationType>[] = Object.values(OperationType).map(value => ({
    value,
    label: operationTypeLocalized[value],
}));

const resourceTypeOptions: SelectOption<ResourceType>[] = Object.values(ResourceType).map(value => ({
    value,
    label: resourceTypeLocalized[value],
}));

const SELECTION_RANGE_DAYS = 30;

function KeycloakLogFilters({model, onChange, availableFilters}: FiltersProps<KeycloakLogFiltersViewModel, KeycloakLogFilterName>) {
    const {locale} = useLocale();
    const availableDateRangeForSelection = {from: addDaysToDate(getTodayAsDate(), -SELECTION_RANGE_DAYS)};

    const filters: FilterGroupNewFilter<KeycloakLogFiltersViewModel, KeycloakLogFilterName>[] = [
        {
            filterName: 'operator',
            modelField: 'operator',
            component: (props: FilterProps<string>) => <TextFilterFullWidth {...props} inputPlaceholder={localized.operatorPlaceholder} />,
            collapseOnMobile: false,
        },
        {
            filterName: 'targetUser',
            modelField: 'targetUser',
            component: (props: FilterProps<string>) => (
                <TextFilterFullWidth {...props} inputPlaceholder={localized.targetUserPlaceholder} />
            ),
            collapseOnMobile: false,
        },
        {
            filterName: 'opretationType',
            modelField: 'opretationType',
            component: (props: FilterProps<OperationType[]>) => (
                <SelectFilter {...props} label={localized.operationType} options={operationTypeOptions} multiple fullWidthInput />
            ),
        },
        {
            filterName: 'resourceType',
            modelField: 'resourceType',
            component: (props: FilterProps<ResourceType[]>) => (
                <SelectFilter {...props} label={localized.resourceType} options={resourceTypeOptions} multiple fullWidthInput />
            ),
        },
        {
            filterName: 'date',
            modelField: 'date',
            component: (props: FilterProps<DateRangeFilterValue>) => (
                <DateRangeFilter
                    {...props}
                    width="full"
                    locale={locale as Locale}
                    availableDateRangeForSelection={availableDateRangeForSelection}
                />
            ),
        },
    ];

    return (
        <FilterGroupNew
            mode="update-on-submit"
            allFilters={filters}
            model={model}
            onChange={onChange}
            availableFilters={availableFilters}
        />
    );
}

function KeycloakLogFiltersAdapter({
    model,
    onChange,
    ...props
}: Omit<QueryFilterAdapterProps<KeycloakLogFilterKeys, KeycloakLogFilterName, KeycloakLogTextFilterKeys>, 'submitButton'>) {
    const filterViewModel: KeycloakLogFiltersViewModel = {
        operator: model?.operator_uuid as string,
        targetUser: model?.target_user_uuid as string,
        opretationType: model?.operation_type as OperationType[],
        resourceType: model?.resource_type as ResourceType[],
        date: {
            from: model?.['date.from'] as number,
            to: model?.['date.to'] as number,
        },
    };

    function handleChange(nextModel: KeycloakLogFiltersViewModel) {
        const result: QueryFilterAdapterModel<KeycloakLogFilterKeys, KeycloakLogTextFilterKeys> = {
            operator_uuid: nextModel?.operator,
            target_user_uuid: nextModel?.targetUser,
            operation_type: nextModel?.opretationType?.length ? nextModel.opretationType : undefined,
            resource_type: nextModel?.resourceType?.length ? nextModel.resourceType : undefined,
            'date.from': nextModel?.date?.from,
            'date.to': nextModel?.date?.to,
        };

        onChange(result);
    }

    return <KeycloakLogFilters model={filterViewModel} onChange={handleChange} {...props} />;
}

export const KeycloakLogQueryFilter = withQueryFilter<KeycloakLogFilterKeys, KeycloakLogFilterName, KeycloakLogTextFilterKeys>(
    KeycloakLogFiltersAdapter
);
